import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Settings as SettingsIcon,
  AccountBalance as AccountBalanceIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  PersonPin as PersonPinIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  LockOutlined as LockOutlinedIcon,
  PriceChange as PriceChangeIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useMaterialUIController, useGlobalMDAlert } from 'context';
import { useApi } from 'lib/api';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import tiposDocumentosJson from 'utils/tiposDocumentos.json';

const opcionesTipoContribuyente =
  (Array.isArray(tiposDocumentosJson) &&
    tiposDocumentosJson.find((e) => Array.isArray(e?.tipocontribuyentecliente))
      ?.tipocontribuyentecliente) ||
  ['formal', 'informal'];

/** Permite tipear decimales sin perder foco (evita type="number" + valor numérico en cada tecla). */
const RE_TEXTO_DECIMAL = /^\d*\.?\d*$/;

const parsearMontoTexto = (raw) => {
  const s = String(raw ?? '').trim().replace(',', '.');
  if (s === '' || s === '.') return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const montoApiATexto = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? String(n) : '0';
};

/** Deben vivir fuera del componente: si se declaran adentro, cada render cambia la identidad del tipo y React desmonta el árbol (inputs pierden foco). */
function ConfiguracionClienteSectionHeader({ icon, label }) {
  return (
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        py: 1.6,
        borderBottom: `1px solid ${FP.color.border}`,
        bgcolor: '#FAFBFE',
        display: 'flex',
        alignItems: 'center',
        gap: 1.2,
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1.2,
          bgcolor: FP.color.accentBlueSoft,
          color: FP.color.accentBlue,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
        {label}
      </Typography>
    </Box>
  );
}

function ConfiguracionClienteFpCard({ children, sx: extraSx }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
        overflow: 'hidden',
        ...extraSx,
      }}
    >
      {children}
    </Card>
  );
}

const ConfiguracionClientes = ({ clienteId: clienteIdProp, onGuardar }) => {
  const [controller] = useMaterialUIController();
  const { nombreNegocio, roleDetalles } = controller;
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const showSuccess = (message) =>
    showAlert({ message, color: 'success', autoHideDuration: 3000 });
  const showError = (message) =>
    showAlert({ message, color: 'error', autoHideDuration: 4000 });
  const selectedCompany = nombreNegocio;

  // Modo embebido (dentro de RegistroClientes) vs standalone (acceso por ruta).
  const embedded = Boolean(clienteIdProp) || typeof onGuardar === 'function';
  const standalone = !embedded;

  // Estados del formulario
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [limiteCreditoTexto, setLimiteCreditoTexto] = useState('0');
  const [limiteCompraTexto, setLimiteCompraTexto] = useState('0');
  const [diasCredito, setDiasCredito] = useState(30);
  const [montoMaximoFacturacionTexto, setMontoMaximoFacturacionTexto] = useState('0');
  const [vendedor, setVendedor] = useState('');

  // Estados auxiliares
  const [vendedores, setVendedores] = useState([]);
  const [listasPrecios, setListasPrecios] = useState([]);
  const [listaPrecioCliente, setListaPrecioCliente] = useState('');
  const [tipoContribuyente, setTipoContribuyente] = useState('formal');
  const [cargando, setCargando] = useState(false);

  // Búsqueda (modo standalone)
  const [tipoBusqueda, setTipoBusqueda] = useState('Cliente');
  const [dialogoBusqueda, setDialogoBusqueda] = useState(false);
  const [descripcionBusqueda, setDescripcionBusqueda] = useState('');
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);

  const opcionesDiasCredito = [30, 60, 90, 120, 150, 180];

  useEffect(() => {
    cargarVendedores();
    cargarListasPrecios();
  }, [selectedCompany]);

  useEffect(() => {
    if (clienteIdProp) {
      cargarDatosCliente(clienteIdProp);
    }
  }, [clienteIdProp]);

  // Carga el vendedor cuando los datos del cliente y la lista de vendedores están disponibles.
  useEffect(() => {
    if (clienteSeleccionado && vendedores.length > 0) {
      const vendedorId = clienteSeleccionado.vendedor ? String(clienteSeleccionado.vendedor) : '';
      const vendedorEncontrado = vendedores.find((v) => v.texto.startsWith(vendedorId + '-'));
      setVendedor(vendedorEncontrado ? vendedorEncontrado.texto : '');
    }
  }, [clienteSeleccionado, vendedores]);

  const cargarDatosCliente = async (id) => {
    setCargando(true);
    try {
      const { ok, data } = await post('/api/clientesbackend_obtenerporid', {
        id_empresa: selectedCompany,
        id: id,
      });

      if (ok) {
        const cliente = data.cliente;
        setClienteSeleccionado(cliente);

        setLimiteCreditoTexto(montoApiATexto(cliente.limcredito));
        setLimiteCompraTexto(montoApiATexto(cliente.limcompra));
        setListaPrecioCliente(cliente.lista_precio_id ? String(cliente.lista_precio_id) : '');
        const tipocontrib = String(cliente.tipocontribuyente || 'formal').toLowerCase();
        setTipoContribuyente(
          opcionesTipoContribuyente.includes(tipocontrib) ? tipocontrib : 'formal',
        );
      } else {
        showError('Error al cargar los datos del cliente');
      }
    } catch (error) {
      showError('Error de conexión al cargar cliente');
    } finally {
      setCargando(false);
    }
  };

  const cargarVendedores = async () => {
    try {
      const { ok, data } = await post('/api/clientesbackend_obtenervendedores', {
        id_empresa: selectedCompany,
      });

      if (ok) {
        setVendedores(data.vendedores || []);
      } else {
        showError(data.Error || 'Error al cargar vendedores');
      }
    } catch (error) {
      showError('Error de conexión al cargar vendedores');
    }
  };

  const cargarListasPrecios = async () => {
    try {
      const { ok, data } = await post('/api/productosbackend_listarlistasprecios', {
        id_empresa: selectedCompany,
      });
      if (ok) {
        setListasPrecios(data.listas || []);
      }
    } catch (error) {
      // silencioso: la pantalla seguirá funcionando sin listas de precios
    }
  };

  const validarFormulario = () => {
    if (!clienteSeleccionado) {
      showError('Debe seleccionar un cliente o suplidor primero');
      return false;
    }
    return true;
  };

  const handleGuardarConfiguracion = async () => {
    if (!validarFormulario()) return;

    try {
      const { ok, data } = await post('/api/clientesbackend_modificar', {
        id_empresa: selectedCompany,
        nombre: clienteSeleccionado.descrip,
        direccion: clienteSeleccionado.direccion,
        ciudad: clienteSeleccionado.ciudad,
        telefono: clienteSeleccionado.telefono,
        fax: clienteSeleccionado.fax,
        email: clienteSeleccionado.email,
        vendedor,
        rnc: clienteSeleccionado.rnc,
        limcredito: parsearMontoTexto(limiteCreditoTexto),
        limcompra: parsearMontoTexto(limiteCompraTexto),
        desc1: clienteSeleccionado.desc1 || 0,
        desc2: clienteSeleccionado.desc2 || 0,
        desc3: clienteSeleccionado.desc3 || 0,
        tipo: clienteSeleccionado.tiposuplidor,
        area:
          clienteSeleccionado.tiposuplidor === 'Cliente' && clienteSeleccionado.area != null
            ? String(clienteSeleccionado.area)
            : null,
        lista_precio_id:
          clienteSeleccionado.tiposuplidor === 'Cliente' && listaPrecioCliente
            ? parseInt(listaPrecioCliente, 10)
            : null,
        tipocontribuyente: tipoContribuyente || 'formal',
      });

      if (ok) {
        showSuccess('Configuración guardada exitosamente');
        if (onGuardar) {
          limpiarFormulario();
          onGuardar();
        } else if (standalone) {
          // En standalone, recargamos los datos del cliente actual.
          if (clienteSeleccionado?.id) {
            cargarDatosCliente(clienteSeleccionado.id);
          }
        }
      } else {
        showError(data.Error || 'Error al guardar la configuración');
      }
    } catch (error) {
      showError('Error de conexión al guardar');
    }
  };

  const limpiarFormulario = () => {
    setLimiteCreditoTexto('0');
    setLimiteCompraTexto('0');
    setListaPrecioCliente('');
    setTipoContribuyente('formal');
    setDiasCredito(30);
    setMontoMaximoFacturacionTexto('0');
    setVendedor('');
    setClienteSeleccionado(null);
  };

  const onMontoDigitadoChange = (setter) => (e) => {
    const next = e.target.value;
    if (next === '' || RE_TEXTO_DECIMAL.test(next)) setter(next);
  };

  const onMontoDigitadoBlur = (setter) => () => {
    setter((prev) => {
      const t = String(prev).trim();
      if (t === '') return '0';
      return String(parsearMontoTexto(t));
    });
  };

  const abrirBusqueda = () => {
    setDialogoBusqueda(true);
    setResultadosBusqueda([]);
    setDescripcionBusqueda('');
  };

  const realizarBusqueda = async () => {
    try {
      const { ok, data } = await post('/api/clientesbackend_buscar', {
        id_empresa: selectedCompany,
        tipo: tipoBusqueda,
        descripcion: descripcionBusqueda,
      });

      if (ok) {
        setResultadosBusqueda(data.resultados || []);
      } else {
        showError(data.Error || 'Error al buscar');
      }
    } catch (error) {
      showError('Error de conexión al buscar');
    }
  };

  const seleccionarClienteDesdeBusqueda = async (id) => {
    await cargarDatosCliente(id);
    setDialogoBusqueda(false);
  };

  const tienePermiso = tienePermisosModulo(roleDetalles, 'configuracionclientessuplidores');

  // Botón primario reutilizable estilo FinancePro (navy lleno, sin sombra).
  const primaryBtnSx = {
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    borderRadius: 1.4,
    height: 46,
    px: 2.4,
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
  };

  // Botón secundario / outlined (blanco con borde suave).
  const outlineBtnSx = {
    borderColor: FP.color.border,
    color: FP.color.textPrimary,
    bgcolor: '#FFFFFF',
    borderRadius: 1.4,
    height: 46,
    px: 2.4,
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
  };

  if (!tienePermiso) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          bgcolor: FP.color.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Card
          elevation={0}
          sx={{
            maxWidth: 560,
            width: '100%',
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            textAlign: 'center',
            p: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1.6,
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: FP.color.dangerSoft,
              color: FP.color.danger,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.25rem',
              color: FP.color.textPrimary,
              mb: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Acceso Denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
            No tiene permisos para acceder al módulo de Configuración de Clientes y Suplidores.
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
            Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
          </Typography>
        </Card>
      </Box>
    );
  }

  const tipoActual = clienteSeleccionado?.tiposuplidor || 'Cliente';

  const contenido = (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Tarjeta del cliente seleccionado / buscador */}
      <ConfiguracionClienteFpCard sx={{ mb: 3 }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {tipoActual === 'Cliente' ? (
              <PersonIcon sx={{ fontSize: 18 }} />
            ) : (
              <BusinessIcon sx={{ fontSize: 18 }} />
            )}
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            Configuración del {tipoActual}
          </Typography>
          {clienteSeleccionado && (
            <Chip
              size="small"
              icon={tipoActual === 'Cliente' ? <PersonIcon /> : <BusinessIcon />}
              label={tipoActual}
              sx={{
                ml: 1,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                fontWeight: 700,
                '& .MuiChip-icon': { color: FP.color.accentBlue },
              }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600 }}>
            {clienteSeleccionado ? 'Cliente seleccionado' : 'Sin cliente seleccionado'}
          </Typography>
        </Box>

        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={2.5} alignItems="center">
            <Grid item xs={12} sm={standalone ? 3 : 4}>
              <TextField
                fullWidth
                label="Nombre"
                value={clienteSeleccionado?.descrip || ''}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                      <PersonPinIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                    </Box>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                placeholder="Seleccione un cliente o suplidor"
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={standalone ? 3 : 4}>
              <TextField
                fullWidth
                label="RNC / Cédula"
                value={clienteSeleccionado?.rnc || ''}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={standalone ? 3 : 4}>
              <TextField
                fullWidth
                label="Ciudad"
                value={clienteSeleccionado?.ciudad || ''}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                      <LocationIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                    </Box>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            {standalone && (
              <Grid item xs={12} sm={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={abrirBusqueda}
                  sx={outlineBtnSx}
                >
                  Buscar
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </ConfiguracionClienteFpCard>

      {cargando ? (
        <ConfiguracionClienteFpCard>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: FP.color.textSecondary }}>
              Cargando configuración…
            </Typography>
          </CardContent>
        </ConfiguracionClienteFpCard>
      ) : !clienteSeleccionado ? (
        <ConfiguracionClienteFpCard>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
              }}
            >
              <SettingsIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 0.6 }}>
              Seleccione un cliente o suplidor
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}>
              {standalone
                ? 'Use el botón "Buscar" para encontrar un cliente o suplidor y configurar sus parámetros.'
                : 'Para configurar los parámetros, primero debe seleccionarlo desde el listado o búsqueda en la pestaña Registro.'}
            </Typography>
          </CardContent>
        </ConfiguracionClienteFpCard>
      ) : (
        <Grid container spacing={3}>
          {/* Límites Financieros */}
          <Grid item xs={12} md={6}>
            <ConfiguracionClienteFpCard sx={{ height: '100%' }}>
              <ConfiguracionClienteSectionHeader
                icon={<AccountBalanceIcon sx={{ fontSize: 18 }} />}
                label="Límites Financieros"
              />
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                {tipoActual === 'Cliente' ? (
                  <TextField
                    fullWidth
                    label="Límite de Crédito"
                    value={limiteCreditoTexto}
                    onChange={onMontoDigitadoChange(setLimiteCreditoTexto)}
                    onBlur={onMontoDigitadoBlur(setLimiteCreditoTexto)}
                    type="text"
                    inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Monto máximo de crédito que se puede otorgar al cliente."
                    sx={{ ...fpInputSx, mb: 2.5 }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Límite de Compra"
                    value={limiteCompraTexto}
                    onChange={onMontoDigitadoChange(setLimiteCompraTexto)}
                    onBlur={onMontoDigitadoBlur(setLimiteCompraTexto)}
                    type="text"
                    inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Monto máximo de compra permitido al suplidor."
                    sx={{ ...fpInputSx, mb: 2.5 }}
                  />
                )}

                <TextField
                  fullWidth
                  label="Monto Máximo de Facturación"
                  value={montoMaximoFacturacionTexto}
                  onChange={onMontoDigitadoChange(setMontoMaximoFacturacionTexto)}
                  onBlur={onMontoDigitadoBlur(setMontoMaximoFacturacionTexto)}
                  type="text"
                  inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Monto máximo permitido en una factura individual."
                  sx={fpInputSx}
                />
              </CardContent>
            </ConfiguracionClienteFpCard>
          </Grid>

          {/* Ubicación y Términos */}
          <Grid item xs={12} md={6}>
            <ConfiguracionClienteFpCard sx={{ height: '100%' }}>
              <ConfiguracionClienteSectionHeader
                icon={<LocationIcon sx={{ fontSize: 18 }} />}
                label="Ubicación y Términos"
              />
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <FormControl fullWidth sx={{ ...fpInputSx, mb: 2.5 }}>
                  <InputLabel id="vendedor-label" shrink>
                    Vendedor
                  </InputLabel>
                  <Select
                    labelId="vendedor-label"
                    value={vendedor}
                    onChange={(e) => setVendedor(e.target.value)}
                    label="Vendedor"
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: 0.5 }}>
                        <PersonPinIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">
                      <em>Sin vendedor asignado</em>
                    </MenuItem>
                    {vendedores.map((vend) => (
                      <MenuItem key={vend.id} value={vend.texto}>
                        {vend.texto}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: 'block', color: FP.color.textSecondary }}
                  >
                    Vendedor asignado al cliente / suplidor.
                  </Typography>
                </FormControl>

                <FormControl fullWidth sx={{ ...fpInputSx, mb: 2.5 }}>
                  <InputLabel id="tipo-contribuyente-label" shrink>
                    Tipo (Formal / Informal)
                  </InputLabel>
                  <Select
                    labelId="tipo-contribuyente-label"
                    value={tipoContribuyente}
                    onChange={(e) => setTipoContribuyente(e.target.value)}
                    label="Tipo (Formal / Informal)"
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: 0.5 }}>
                        <BadgeIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                      </InputAdornment>
                    }
                  >
                    {opcionesTipoContribuyente.map((opcion) => (
                      <MenuItem key={opcion} value={opcion}>
                        {opcion.charAt(0).toUpperCase() + opcion.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: 'block', color: FP.color.textSecondary }}
                  >
                    Clasificación del contribuyente para la configuración del cliente.
                  </Typography>
                </FormControl>

                {tipoActual === 'Cliente' && (
                  <FormControl fullWidth sx={{ ...fpInputSx, mb: 2.5 }}>
                    <InputLabel id="lista-precio-label" shrink>
                      Lista de Precios
                    </InputLabel>
                    <Select
                      labelId="lista-precio-label"
                      value={listaPrecioCliente}
                      onChange={(e) => setListaPrecioCliente(e.target.value)}
                      label="Lista de Precios"
                      displayEmpty
                      startAdornment={
                        <InputAdornment position="start" sx={{ ml: 0.5 }}>
                          <PriceChangeIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="">
                        <em>Sin lista asignada (precio normal)</em>
                      </MenuItem>
                      {listasPrecios.map((l) => (
                        <MenuItem key={l.id} value={String(l.id)}>
                          {l.codigo} – {l.descripcion || '-'}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: 'block', color: FP.color.textSecondary }}
                    >
                      Lista que se aplicará por defecto al cliente en facturación.
                    </Typography>
                  </FormControl>
                )}

                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="dias-credito-label" shrink>
                    Días de Crédito
                  </InputLabel>
                  <Select
                    labelId="dias-credito-label"
                    value={diasCredito}
                    onChange={(e) => setDiasCredito(e.target.value)}
                    label="Días de Crédito"
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: 0.5 }}>
                        <CalendarIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                      </InputAdornment>
                    }
                  >
                    {opcionesDiasCredito.map((dias) => (
                      <MenuItem key={dias} value={dias}>
                        {dias} días
                      </MenuItem>
                    ))}
                    <MenuItem value={999}>Más de 180 días</MenuItem>
                  </Select>
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: 'block', color: FP.color.textSecondary }}
                  >
                    Plazo de crédito otorgado al cliente para el pago de facturas.
                  </Typography>
                </FormControl>
              </CardContent>
            </ConfiguracionClienteFpCard>
          </Grid>

          {/* Resumen de Configuración */}
          <Grid item xs={12}>
            <ConfiguracionClienteFpCard>
              <ConfiguracionClienteSectionHeader
                icon={<SettingsIcon sx={{ fontSize: 18 }} />}
                label="Resumen de Configuración"
              />
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      sx={{
                        color: FP.color.textSecondary,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mb: 0.5,
                      }}
                    >
                      {tipoActual === 'Cliente' ? 'Límite Crédito' : 'Límite Compra'}
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 800, fontSize: '1.1rem', color: FP.color.textPrimary }}
                    >
                      $
                      {(tipoActual === 'Cliente'
                        ? parsearMontoTexto(limiteCreditoTexto)
                        : parsearMontoTexto(limiteCompraTexto)
                      ).toLocaleString(
                        'es-DO',
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                      )}
                    </Typography>
                  </Grid>
                  {tipoActual === 'Cliente' && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography
                        sx={{
                          color: FP.color.textSecondary,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          mb: 0.5,
                        }}
                      >
                        Lista de Precios
                      </Typography>
                      <Typography
                        sx={{ fontWeight: 800, fontSize: '1.1rem', color: FP.color.textPrimary }}
                      >
                        {listaPrecioCliente
                          ? (() => {
                              const lp = listasPrecios.find(
                                (l) => String(l.id) === String(listaPrecioCliente),
                              );
                              return lp ? `${lp.codigo} – ${lp.descripcion || ''}` : `ID ${listaPrecioCliente}`;
                            })()
                          : 'Precio normal'}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      sx={{
                        color: FP.color.textSecondary,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mb: 0.5,
                      }}
                    >
                      Tipo Contribuyente
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 800, fontSize: '1.1rem', color: FP.color.textPrimary }}
                    >
                      {tipoContribuyente
                        ? tipoContribuyente.charAt(0).toUpperCase() + tipoContribuyente.slice(1)
                        : 'Formal'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      sx={{
                        color: FP.color.textSecondary,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mb: 0.5,
                      }}
                    >
                      Días de Crédito
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 800, fontSize: '1.1rem', color: FP.color.textPrimary }}
                    >
                      {diasCredito === 999 ? 'Más de 180' : diasCredito} días
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      sx={{
                        color: FP.color.textSecondary,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mb: 0.5,
                      }}
                    >
                      Máximo Facturación
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 800, fontSize: '1.1rem', color: FP.color.textPrimary }}
                    >
                      $
                      {parsearMontoTexto(montoMaximoFacturacionTexto).toLocaleString('es-DO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Botones de acción */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.2,
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                    mt: 3,
                    pt: 2.5,
                    borderTop: `1px solid ${FP.color.border}`,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={limpiarFormulario}
                    sx={outlineBtnSx}
                  >
                    Limpiar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleGuardarConfiguracion}
                    sx={primaryBtnSx}
                  >
                    Guardar Configuración
                  </Button>
                </Box>
              </CardContent>
            </ConfiguracionClienteFpCard>
          </Grid>
        </Grid>
      )}

      {/* Diálogo de Búsqueda (modo standalone) */}
      <Dialog
        open={dialogoBusqueda}
        onClose={() => setDialogoBusqueda(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }}>
              Buscar {tipoBusqueda === 'Cliente' ? 'Clientes' : 'Suplidores'}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDialogoBusqueda(false)}
            size="small"
            sx={{
              color: FP.color.textSecondary,
              border: `1px solid ${FP.color.border}`,
              borderRadius: 1.2,
              width: 32,
              height: 32,
              '&:hover': { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
            }}
          >
            <ClearIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth sx={fpInputSx}>
                <InputLabel id="tipo-busqueda-label">Tipo</InputLabel>
                <Select
                  labelId="tipo-busqueda-label"
                  value={tipoBusqueda}
                  onChange={(e) => setTipoBusqueda(e.target.value)}
                  label="Tipo"
                >
                  <MenuItem value="Cliente">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      Cliente
                    </Box>
                  </MenuItem>
                  <MenuItem value="Suplidor">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" />
                      Suplidor
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Descripción"
                value={descripcionBusqueda}
                onChange={(e) => setDescripcionBusqueda(e.target.value)}
                placeholder="Buscar por nombre…"
                InputLabelProps={{ shrink: true }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') realizarBusqueda();
                }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={realizarBusqueda}
                sx={primaryBtnSx}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              boxShadow: 'none',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: FP.color.surfaceAlt }}>
                  {['ID', 'Nombre', 'RNC', 'Ciudad'].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 700,
                        color: FP.color.textSecondary,
                        textTransform: 'uppercase',
                        fontSize: '0.72rem',
                        letterSpacing: '0.08em',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: FP.color.textSecondary,
                      textTransform: 'uppercase',
                      fontSize: '0.72rem',
                      letterSpacing: '0.08em',
                    }}
                  >
                    Acción
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resultadosBusqueda.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ color: FP.color.textMuted, py: 4, fontSize: '0.85rem' }}
                    >
                      Sin resultados. Escriba una descripción y presione Buscar.
                    </TableCell>
                  </TableRow>
                ) : (
                  resultadosBusqueda.map((cliente) => (
                    <TableRow
                      key={cliente.id}
                      sx={{ '&:hover': { bgcolor: FP.color.surface } }}
                    >
                      <TableCell sx={{ color: FP.color.textPrimary }}>{cliente.id}</TableCell>
                      <TableCell sx={{ color: FP.color.textPrimary, fontWeight: 600 }}>
                        {cliente.descrip}
                      </TableCell>
                      <TableCell sx={{ color: FP.color.textPrimary }}>{cliente.rnc}</TableCell>
                      <TableCell sx={{ color: FP.color.textPrimary }}>{cliente.ciudad}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                          onClick={() => seleccionarClienteDesdeBusqueda(cliente.id)}
                          sx={{
                            bgcolor: FP.color.navy,
                            color: '#FFFFFF',
                            borderRadius: 1.2,
                            textTransform: 'none',
                            fontWeight: 700,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                          }}
                        >
                          Seleccionar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${FP.color.border}` }}>
          <Button onClick={() => setDialogoBusqueda(false)} sx={outlineBtnSx}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // Render: en standalone agregamos el PageHeader y el fondo de pantalla completo,
  // en embedded devolvemos sólo el contenido para integrarse a la tab del padre.
  if (standalone) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Configuración Clientes y Suplidores" subtitle="Parámetros y términos" />
        {contenido}
      </Box>
    );
  }

  return contenido;
};

export default ConfiguracionClientes;
